import { type } from 'arktype';
import { isDateRangeOrdered, rruleSchema } from '@zag/common/validity';

/** Query schema for filtering employee skills */
export const employeeSkillQuerySchema = type({
  'employeeId?': 'string',
  'skillId?': 'string',
});

export type EmployeeSkillQuery = typeof employeeSkillQuerySchema.t;

/** Schema for creating an employee skill assignment */
export const createEmployeeSkillSchema = type({
  employeeId: type('string').configure({
    description: 'ID of the employee gaining the skill.',
    examples: ['employee_abc123'],
  }),
  skillId: type('string').configure({
    description: 'ID of the skill being attached.',
    examples: ['skill_abc123'],
  }),
  'acquiredDate?': type('string | null | undefined').configure({
    description: 'Date the skill was acquired, as ISO 8601 (YYYY-MM-DD). `null` clears the value.',
    examples: ['2026-01-15'],
  }),
  'expiryDate?': type('string | null | undefined').configure({
    description: 'Date the skill expires, as ISO 8601 (YYYY-MM-DD). `null` clears the value.',
    examples: ['2028-01-15'],
  }),
  'rrule?': rruleSchema.configure({
    description: 'Optional iCal RRULE body refining which dates the person can exercise this skill.',
    examples: ['FREQ=WEEKLY;BYDAY=MO'],
  }),
  'rating?': type('number | null | undefined').configure({
    description: "Proficiency rating on the skill's rating scale. `null` clears the rating.",
    examples: [4],
  }),
  'note?': type('string | null | undefined').configure({
    description: 'Optional free-text note for this skill assignment.',
    examples: ['Certified by external provider'],
  }),
})
  .narrow(
    (value, ctx) =>
      isDateRangeOrdered(value.acquiredDate, value.expiryDate) || ctx.mustBe('a date range with expiryDate on or after acquiredDate')
  )
  .configure({ description: 'Payload for attaching a skill to an employee with optional dates and rating.' });

export type CreateEmployeeSkill = typeof createEmployeeSkillSchema.t;

/** Schema for updating an employee skill assignment */
export const updateEmployeeSkillSchema = type({
  'acquiredDate?': type('string | null | undefined').configure({
    description: 'Updated acquisition date (YYYY-MM-DD). `null` clears the value.',
    examples: ['2026-01-15'],
  }),
  'expiryDate?': type('string | null | undefined').configure({
    description: 'Updated expiry date (YYYY-MM-DD). `null` clears the value.',
    examples: ['2028-01-15'],
  }),
  'rrule?': rruleSchema.configure({
    description: 'Optional iCal RRULE body refining which dates the person can exercise this skill.',
    examples: ['FREQ=WEEKLY;BYDAY=MO'],
  }),
  'rating?': type('number | null | undefined').configure({
    description: 'Updated proficiency rating. `null` clears the rating.',
    examples: [4],
  }),
  'note?': type('string | null | undefined').configure({
    description: 'Updated free-text note. `null` clears the note.',
    examples: ['Recertified after refresher course'],
  }),
})
  .narrow(
    (value, ctx) =>
      isDateRangeOrdered(value.acquiredDate, value.expiryDate) || ctx.mustBe('a date range with expiryDate on or after acquiredDate')
  )
  .configure({ description: 'Partial update of an employee-skill assignment — fields omitted from the body remain unchanged.' });

export type UpdateEmployeeSkill = typeof updateEmployeeSkillSchema.t;
